import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Menu } from 'lucide-react';
import { AshlarLogo } from '@/components/AshlarLogo';
import { Container } from '@/components/g5';
import { AuraButton, ThemeSwitch } from '@/components/aura';

interface NavLink {
  label: string;
  href: string;
  homeAnchor?: string;
}

// Two doors, consumer first: the marketplace fork puts "Find an Appraiser"
// ahead of the software pitch. Features/News/Glossary live one level down
// (under For Appraisers and Resources) to keep the bar scannable.
const navLinks = [
  { label: 'Find an Appraiser', href: '/find-an-appraiser' },
  { label: 'For Appraisers', href: '/for-appraisers' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'About', href: '/about' },
  { label: 'Resources', href: '/resources' },
] satisfies NavLink[];

const navLinkCls = 'text-sm transition-colors text-[color:var(--g5-sub)] hover:text-[color:var(--g5-ink)]';
const headerStyle = { borderColor: 'var(--g5-line)', background: 'color-mix(in srgb, var(--g5-bg) 85%, transparent)' } as const;

export function LandingHeader() {
  const { isAuthenticated, authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleNavClick = (link: NavLink) => {
    setIsOpen(false);
    if (location.pathname === '/' && link.homeAnchor) {
      const id = link.homeAnchor.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    navigate(link.href);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  };

  if (authLoading) {
    return (
      <header className="sticky top-0 z-50 w-full border-b backdrop-blur" style={headerStyle}>
        <Container size="wide" className="flex h-16 items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <div className="flex gap-2"><Skeleton className="h-9 w-20" /><Skeleton className="h-9 w-32" /></div>
        </Container>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b backdrop-blur" style={headerStyle}>
      <Container size="wide" className="flex h-16 items-center justify-between gap-6">
        <button onClick={handleLogoClick} className="shrink-0 transition-opacity hover:opacity-80" aria-label="Ashlar home">
          <AshlarLogo className="h-8 w-auto" />
        </button>

        <nav className="hidden items-center gap-6 lg:flex">
          {navLinks.map((link) => (
            <button key={link.href} onClick={() => handleNavClick(link)} className={navLinkCls}>{link.label}</button>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-4 lg:flex">
          {isAuthenticated ? (
            <AuraButton size="sm" onClick={() => navigate('/dashboard')}>Go to dashboard</AuraButton>
          ) : (
            <>
              <button onClick={() => navigate('/login')} className="text-sm font-medium text-[color:var(--g5-ink)]">Login</button>
              <AuraButton size="sm" onClick={() => navigate('/login?tab=signup')}>Get early access</AuraButton>
            </>
          )}
          {/* utility, set apart from the marketing actions */}
          <span className="h-5 w-px" style={{ background: 'var(--g5-line)' }} />
          <ThemeSwitch />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <ThemeSwitch />
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-150 hover:bg-[color:var(--g5-tint)]"
                style={{ color: 'var(--g5-ink)' }}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="ashlar-public w-[300px] sm:w-[360px]" style={{ background: 'var(--g5-bg)', color: 'var(--g5-ink)', borderColor: 'var(--g5-line)' }}>
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Site navigation links and account actions</SheetDescription>
              <div className="mt-8 flex flex-col gap-6">
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <button key={link.href} onClick={() => handleNavClick(link)} className="text-left font-display text-lg font-semibold text-[color:var(--g5-ink)]">{link.label}</button>
                  ))}
                </nav>
                <div className="h-px" style={{ background: 'var(--g5-line)' }} />
                <div className="flex flex-col gap-3">
                  {isAuthenticated ? (
                    <AuraButton onClick={() => { navigate('/dashboard'); setIsOpen(false); }}>Go to dashboard</AuraButton>
                  ) : (
                    <>
                      <AuraButton variant="soft" onClick={() => { navigate('/login'); setIsOpen(false); }}>Login</AuraButton>
                      <AuraButton onClick={() => { navigate('/login?tab=signup'); setIsOpen(false); }}>Get early access</AuraButton>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </Container>
    </header>
  );
}
